import {AxiosError, AxiosResponse} from 'axios';
import http from '@/axios';
import {
    AuthProvider,
    LoginSuccessResponse,
    RoutingBootstrapResponse,
    ServerType,
} from '@/model/routing';
import {RoutingBootstrapService} from '@/services/routing-bootstrap-service';
import {StandUrls} from '@/utils/stand-urls';

interface DualLoginTarget {
    serverType: ServerType;
    apiBaseUrl: string;
    wsBaseUrl: string;
    sticky: boolean;
}

interface DualLoginAttempt {
    target: DualLoginTarget;
    response: AxiosResponse<LoginSuccessResponse>;
    meta: LoginSuccessResponse;
}

/**
 * AOF-2949: silent dual-login across RU / FOREIGN without server UI.
 * Mobile-only; login endpoints only (never register / create on peer).
 */
export class SilentDualLoginService {

    public static isEnabled(): boolean {
        return RoutingBootstrapService.isMobileApp();
    }

    public static async loginOwn(params: URLSearchParams): Promise<AxiosResponse> {
        return SilentDualLoginService.execute('/api/security/login', params);
    }

    public static async loginWithGoogle(params: URLSearchParams, googleSub?: string): Promise<AxiosResponse> {
        const response = await SilentDualLoginService.execute('/api/security/google/login', params);
        if (googleSub) {
            await SilentDualLoginService.upsertLocator('GOOGLE', googleSub);
        }
        return response;
    }

    public static async loginWithApple(params: URLSearchParams, appleSub?: string): Promise<AxiosResponse> {
        const response = await SilentDualLoginService.execute('/api/security/apple/login', params);
        if (appleSub) {
            await SilentDualLoginService.upsertLocator('APPLE', appleSub);
        }
        return response;
    }

    private static async execute(path: string, params: URLSearchParams): Promise<AxiosResponse> {
        if (!SilentDualLoginService.isEnabled()) {
            return http.post(path, params);
        }

        const sticky = SilentDualLoginService.resolveStickyTarget();
        const peer = SilentDualLoginService.resolvePeerTarget(sticky);
        const previousBaseUrl = http.defaults.baseURL;

        try {
            StandUrls.debugLog(
                `[DualLogin] sticky=${sticky.serverType} api=${sticky.apiBaseUrl}`
                + (peer ? ` peer=${peer.serverType} api=${peer.apiBaseUrl}` : ' peer=none'),
            );
            const stickyResult = await SilentDualLoginService.tryLogin(path, params, sticky);
            if (stickyResult.ok) {
                StandUrls.debugLog(`[DualLogin] ok on sticky ${sticky.serverType}`);
                SilentDualLoginService.applySuccess(stickyResult.attempt);
                return stickyResult.attempt.response;
            }

            if (peer) {
                StandUrls.debugLog(`[DualLogin] sticky failed, trying peer ${peer.serverType}`);
                const peerResult = await SilentDualLoginService.tryLogin(path, params, peer);
                if (peerResult.ok) {
                    StandUrls.debugLog(`[DualLogin] ok on peer ${peer.serverType}`);
                    SilentDualLoginService.applySuccess(peerResult.attempt);
                    return peerResult.attempt.response;
                }
            }

            http.defaults.baseURL = previousBaseUrl || sticky.apiBaseUrl;
            if (stickyResult.error) {
                throw stickyResult.error;
            }
            throw new Error('Dual-login failed');
        } catch (e) {
            if (!RoutingBootstrapService.isServerLocked()) {
                http.defaults.baseURL = previousBaseUrl || sticky.apiBaseUrl;
            }
            throw e;
        }
    }

    private static resolveStickyTarget(): DualLoginTarget {
        const cached = RoutingBootstrapService.getLastResponse();
        const apiBaseUrl = StandUrls.rewrite(RoutingBootstrapService.normalizePublicBaseUrl(
            RoutingBootstrapService.getApiBaseUrl()
            || http.defaults.baseURL
            || process.env.VUE_APP_API_HOST
            || '',
        ));
        const wsBaseUrl = RoutingBootstrapService.normalizePublicBaseUrl(
            StandUrls.rewrite(RoutingBootstrapService.getWsBaseUrl() || `${apiBaseUrl}/api/websocket`),
        );
        const serverType: ServerType = cached?.serverType
            || RoutingBootstrapService.getCachedServerType()
            || 'RU';
        return {serverType, apiBaseUrl, wsBaseUrl, sticky: true};
    }

    private static resolvePeerTarget(sticky: DualLoginTarget): DualLoginTarget | undefined {
        const cached: RoutingBootstrapResponse | undefined = RoutingBootstrapService.getLastResponse();
        let peerServerType: ServerType = cached?.peerServerType
            || (sticky.serverType === 'RU' ? 'FOREIGN' : 'RU');
        // dev/feature/release: never fall back to prod mirland/zazer (cached bootstrap may still be prod).
        const standPeer = StandUrls.peerApi(sticky.apiBaseUrl);
        const fallbackPeerApi: string = standPeer
            || (sticky.serverType === 'RU'
                ? (process.env.VUE_APP_FOREIGN_API_HOST || 'https://mirland.mobi')
                : (process.env.VUE_APP_RU_API_HOST || 'https://zazer.mobi'));
        const rawPeer = standPeer || cached?.peerApiBaseUrl || fallbackPeerApi;
        const peerApi: string = StandUrls.rewrite(rawPeer);
        const apiBaseUrl = RoutingBootstrapService.normalizePublicBaseUrl(peerApi);
        if (!apiBaseUrl || apiBaseUrl === sticky.apiBaseUrl) {
            return undefined;
        }
        return {
            serverType: peerServerType,
            apiBaseUrl,
            wsBaseUrl: RoutingBootstrapService.normalizePublicBaseUrl(`${apiBaseUrl}/api/websocket`),
            sticky: false,
        };
    }

    private static async tryLogin(
        path: string,
        params: URLSearchParams,
        target: DualLoginTarget,
    ): Promise<{ok: true; attempt: DualLoginAttempt} | {ok: false; error?: unknown}> {
        http.defaults.baseURL = target.apiBaseUrl;
        try {
            const response = await http.post<LoginSuccessResponse>(path, params);
            const meta = (response.data && typeof response.data === 'object')
                ? response.data
                : {};
            return {ok: true, attempt: {target, response, meta}};
        } catch (e) {
            const err = e as AxiosError;
            const status = err.response?.status;
            if (status === 401 || status === 404 || status === 406) {
                return {ok: false, error: e};
            }
            if (!err.response) {
                StandUrls.debugLog(`Dual-login network error on ${target.serverType}: ${err.message}`);
                return {ok: false, error: e};
            }
            throw e;
        }
    }

    /**
     * Prefer sticky; otherwise fresher lastActivity / higher level.
     */
    public static resolveConflict(a: DualLoginAttempt, b: DualLoginAttempt): DualLoginAttempt {
        console.warn(
            `Dual-login conflict: both ${a.target.serverType} and ${b.target.serverType} accepted credentials`,
            {a: a.meta, b: b.meta},
        );
        if (a.target.sticky) {
            return a;
        }
        if (b.target.sticky) {
            return b;
        }
        const aTime = SilentDualLoginService.parseActivity(a.meta.lastActivityAt);
        const bTime = SilentDualLoginService.parseActivity(b.meta.lastActivityAt);
        if (aTime !== bTime) {
            return aTime > bTime ? a : b;
        }
        const aLevel = a.meta.level ?? 0;
        const bLevel = b.meta.level ?? 0;
        return aLevel >= bLevel ? a : b;
    }

    private static parseActivity(value?: string | null): number {
        if (!value) {
            return 0;
        }
        const ms = Date.parse(value);
        return Number.isNaN(ms) ? 0 : ms;
    }

    private static applySuccess(attempt: DualLoginAttempt): void {
        RoutingBootstrapService.applyAndLock(
            attempt.target.serverType,
            attempt.target.apiBaseUrl,
            attempt.target.wsBaseUrl,
        );
    }

    private static async upsertLocator(authProvider: AuthProvider, authExternalId: string): Promise<void> {
        const serverType = RoutingBootstrapService.getCachedServerType();
        if (!serverType || !authExternalId) {
            return;
        }
        try {
            await http.post('/api/routing/locator', {
                authProvider,
                authExternalId,
                serverType,
                platform: 'WEB',
            });
        } catch (e) {
            console.warn('Account locator upsert after dual-login failed', e);
        }
    }
}
